/**
 * Pure parts of the car's history (P4-i): how one line of the timeline reads. The API serves structured facts (kind, km, renter,
 * part, severity …); the wording is here, so the modal only lays the lines out.
 */

import { formatInt } from '@/lib/format';
import type { HistorieSkupina, VozidloUdalost } from './api';
import { formatDateTime } from './rezervace-helpers';
import { zpozdeniText } from './vraceni-helpers';

export type UdalostTon = 'ok' | 'warn' | 'danger' | 'neutral' | 'accent';

export interface UdalostRadek {
  nadpis: string;
  ton: UdalostTon;
  /** Facts under the headline, one short line each. */
  radky: string[];
}

export const SKUPINY: { value: HistorieSkupina | undefined; label: string }[] = [
  { value: undefined, label: 'Vše' },
  { value: 'pronajem', label: 'Předání a vrácení' },
  { value: 'zavady', label: 'Závady' },
  { value: 'kontroly', label: 'Kontroly' },
  { value: 'najezd', label: 'Odečty nájezdu' },
  { value: 'vuz', label: 'Zařazení do půjčovny' },
];

const ZAVAZNOST = { nizka: 'drobná', stredni: 'střední', vysoka: 'závažná' } as const;
const km = (n: number) => `${formatInt(n)} km`;

/** `13. 10. 2026 10:00`, or the day alone for an event that carries a date only. */
export const casUdalosti = (u: Pick<VozidloUdalost, 'cas' | 'pouze_datum'>): string =>
  u.pouze_datum ? formatDateTime(u.cas.slice(0, 10)) : formatDateTime(u.cas.slice(0, 16));

export function popisUdalosti(u: VozidloUdalost): UdalostRadek {
  const radky: string[] = [];
  const rez = u.id_rezervace != null ? `rezervace č. ${u.id_rezervace}` : null;
  const zavada = () => {
    if (u.dil) radky.push(`${u.dil}${u.zavaznost ? ` · ${ZAVAZNOST[u.zavaznost]}` : ''}${u.popis ? `: ${u.popis}` : ''}`);
  };
  switch (u.druh) {
    case 'zarazeno':
      return { nadpis: 'Zařazeno do půjčovny', ton: 'accent', radky };
    case 'vyrazeno':
      return { nadpis: 'Vyřazeno z půjčovny', ton: 'neutral', radky };
    case 'vydani':
    case 'vraceni': {
      const ven = u.druh === 'vydani';
      if (u.technicka) radky.push('Technická rezervace');
      else if (u.najemce) radky.push(u.najemce);
      if (u.km != null) radky.push(`Nájezd ${km(u.km)}`);
      if (!ven && u.najeto_km != null) radky.push(`Najeto ${km(u.najeto_km)}`);
      if (!ven && u.po_terminu_min != null) radky.push(`Po termínu o ${zpozdeniText(u.po_terminu_min)}`);
      if (rez) radky.push(rez);
      return { nadpis: ven ? 'Vůz předán' : 'Vůz vrácen', ton: ven ? 'warn' : 'ok', radky };
    }
    case 'zavada_nalezena':
      zavada();
      if (rez) radky.push(`Nalezeno při vrácení, ${rez}`);
      return { nadpis: 'Závada nalezena', ton: u.zavaznost === 'vysoka' ? 'danger' : 'warn', radky };
    case 'zavada_opravena':
      zavada();
      if (u.poznamka) radky.push(`Opraveno: ${u.poznamka}`);
      if (rez) radky.push(`Opraveno při vrácení, ${rez}`);
      return { nadpis: 'Závada opravena', ton: 'ok', radky };
    case 'kontrola_splnena':
      if (u.nazev) radky.push(u.nazev);
      if (u.km != null) radky.push(`Nájezd ${km(u.km)}`);
      if (u.poznamka) radky.push(u.poznamka);
      return { nadpis: 'Kontrola splněna', ton: 'ok', radky };
    case 'odecet':
      if (u.km != null) radky.push(`Nájezd ${km(u.km)}`);
      if (u.poznamka) radky.push(u.poznamka);
      return { nadpis: u.zdroj_odecet === 'korekce' ? 'Oprava tachometru' : 'Odečet tachometru', ton: 'neutral', radky };
  }
}
